import { normalizeProductMedia } from '@/lib/productMedia';
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';

export interface CachedProduct {
  id: number;
  name: string;
  description: string | null;
  media: { url: string; type: string }[];
  price: number | null;
  discount_enabled: boolean;
  discount_price: number | null;
  status: string;
  category_id: number | null;
  product_categories: { id: number; name: string } | null;
}

export interface CachedCategory {
  id: number;
  name: string;
}

interface ProductCacheValue {
  products: CachedProduct[];
  categories: CachedCategory[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const ProductCacheContext = createContext<ProductCacheValue>({
  products: [],
  categories: [],
  loading: true,
  error: null,
  refresh: () => {},
});

export function ProductCacheProvider({ children }: { children: ReactNode }) {
  const [products, setProducts] = useState<CachedProduct[]>([]);
  const [categories, setCategories] = useState<CachedCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const refresh = useCallback(() => {
    setHasFetched(false);
  }, []);

  useEffect(() => {
    if (hasFetched) return;

    let cancelled = false;

    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        const [productsRes, categoriesRes] = await Promise.all([
          supabase
            .from('product_items')
            .select('id, name, media, description, price, discount_enabled, discount_price, status, category_id, product_categories(id, name)')
            .eq('status', 'active')
            .order('created_at', { ascending: false }),
          supabase
            .from('product_categories')
            .select('id, name')
            .order('sort_order'),
        ]);

        if (cancelled) return;

        if (productsRes.error) throw productsRes.error;
        if (categoriesRes.error) throw categoriesRes.error;

        setProducts((productsRes.data ?? []).map(normalizeProductMedia) as unknown as CachedProduct[]);
        setCategories((categoriesRes.data ?? []) as CachedCategory[]);
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message || 'Failed to load products');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setHasFetched(true);
        }
      }
    }

    fetchData();
    return () => { cancelled = true; };
  }, [hasFetched]);

  return (
    <ProductCacheContext.Provider value={{ products, categories, loading, error, refresh }}>
      {children}
    </ProductCacheContext.Provider>
  );
}

export function useProductCache() {
  return useContext(ProductCacheContext);
}
